import {getSectionFeedbackOpenMs, parseSectionWeekId} from '@cip/core/section';
import type {CourseId} from '@cip/core/types';

/**
 * Builds the link for a "Section Feedback" roadmap item: the week-only
 * feedback route, which resolves the SL's own section.
 *
 * @param params.courseId - Course of the roadmap.
 * @param params.completionId - The item's completion id, which holds its
 *   1-based section week.
 * @return The feedback path, or the section page when the completion id is
 *   not a valid section week.
 * @throws Never.
 */
export function getSectionFeedbackItemPath({
  courseId,
  completionId,
}: {
  courseId: CourseId;
  completionId: unknown;
}): string {
  const sectionWeek = parseSectionWeekId(completionId);
  if (sectionWeek === undefined) return `/${courseId}/section`;
  return `/${courseId}/section/sl-section-feedback/${sectionWeek}`;
}

/**
 * The self-check key a "Section Feedback" item is marked complete under once
 * the SL opens it.
 *
 * @param completionId - The item's completion id (its 1-based week).
 * @return The key, or `undefined` when the completion id is not a valid week.
 * @throws Never.
 */
export function getSectionFeedbackCompletionKey(
  completionId: unknown,
): string | undefined {
  const sectionWeek = parseSectionWeekId(completionId);
  return sectionWeek === undefined
    ? undefined
    : `section-feedback-${sectionWeek}`;
}

/**
 * Whether a "Section Feedback" item's week has feedback yet, at the instant
 * the feedback page opens it (`getSectionFeedbackOpenMs`). Closed on
 * `(-∞, open)`, open on the half-open interval `[open, ∞)`.
 *
 * @param params.completionId - The item's completion id (its 1-based week).
 * @param params.firstSectionTimestamp - The course's `firstSectionTimestamp`.
 *   Empty or unparseable (for example while the course loads) means closed.
 * @param params.sectionTimeDelta - Hours from the week's first to last section.
 * @param params.sectionFeedbackDelayDays - The course's feedback delay.
 * @param params.nowMs - The current time in epoch ms. Omitted (server time
 *   not known yet) means closed.
 * @return `true` once the week's feedback is open.
 * @throws Never.
 */
export function isSectionFeedbackItemOpen({
  completionId,
  firstSectionTimestamp,
  sectionTimeDelta,
  sectionFeedbackDelayDays,
  nowMs,
}: {
  completionId: unknown;
  firstSectionTimestamp: string;
  sectionTimeDelta: number;
  sectionFeedbackDelayDays: number;
  nowMs?: number;
}): boolean {
  const sectionWeek = parseSectionWeekId(completionId);
  if (sectionWeek === undefined || nowMs === undefined) return false;
  if (Number.isNaN(Date.parse(firstSectionTimestamp))) return false;
  const openMs = getSectionFeedbackOpenMs({
    firstSectionTimestamp,
    sectionTimeDelta,
    sectionWeek,
    delayDays: sectionFeedbackDelayDays,
  });
  return nowMs >= openMs;
}
